import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { Truck, User, Fuel, MapPin, Phone, Clock, IndianRupee, ShieldCheck, Copy, CheckCircle2 } from "lucide-react";

const STEPS = [
  { key: "pending", label: "Order placed" },
  { key: "approved", label: "Approved" },
  { key: "dispatched", label: "Bowser dispatched" },
  { key: "in_transit", label: "In transit" },
  { key: "delivered", label: "Delivered" },
];

export default function TrackOrder() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const { data: order } = useQuery({
    queryKey: ["track-order", id],
    queryFn: () => api.get(`/orders/${id}`).then((r) => r.data),
    refetchInterval: 8000,
  });

  const confirmMut = useMutation({
    mutationFn: (otp) => api.post(`/orders/${id}/customer-confirm-delivery`, { otp }),
    onSuccess: () => {
      toast.success("Delivery confirmed. Thank you!");
      setConfirmOpen(false);
      setOtpInput("");
      qc.invalidateQueries({ queryKey: ["track-order", id] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Verification failed"),
  });

  const copyOtp = () => {
    if (!order?.delivery_otp) return;
    navigator.clipboard.writeText(order.delivery_otp);
    toast.success("OTP copied");
  };

  if (!order) return <div>Loading...</div>;

  const stepIdx = STEPS.findIndex((s) => s.key === (order.status === "credit_checked" ? "approved" : order.status === "loading" ? "in_transit" : order.status));
  const currentIdx = stepIdx < 0 ? (order.status === "delivered" ? 4 : 0) : stepIdx;

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="track-order">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Order</div>
        <h1 className="font-display text-3xl font-black tracking-tight">{order.code}</h1>
        <div className="mt-2 flex items-center gap-2"><StatusBadge status={order.status} /></div>
      </div>

      {/* Progress */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex items-start gap-4">
                <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${
                  i <= currentIdx ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-muted-foreground"
                }`}>{i + 1}</div>
                <div className="flex-1">
                  <div className={`font-semibold ${i <= currentIdx ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</div>
                  {i === currentIdx && <div className="text-xs text-primary">Current step · updating live</div>}
                  {i === 4 && order.delivered_at && <div className="text-xs text-muted-foreground">Completed {new Date(order.delivered_at).toLocaleString()}</div>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Delivery details</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row icon={Fuel} label="Quantity" value={`${order.quantity_litres} L`} />
            <Row icon={IndianRupee} label="Amount" value={`₹${order.total_amount?.toLocaleString("en-IN")}`} />
            <Row icon={MapPin} label="Site" value={order.site_name} sub={order.site_address} />
            <Row icon={Clock} label="Scheduled" value={order.scheduled_at ? new Date(order.scheduled_at).toLocaleString() : "ASAP"} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Bowser & driver</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row icon={Truck} label="Bowser" value={order.bowser_code || "Not yet assigned"} />
            <Row icon={User} label="Driver" value={order.driver_name || "Not yet assigned"} />
            <Row icon={Phone} label="Contact" value={order.driver_name ? "Call dispatch — 1800-999-1234" : "—"} />
            {order.delivery_otp && ["in_transit", "loading", "dispatched"].includes(order.status) && (
              <div className="mt-4 rounded-2xl border-2 border-primary/50 bg-gradient-to-br from-primary/10 to-primary/5 p-5 text-center shadow-sm" data-testid="otp-card">
                <div className="flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
                  <ShieldCheck size={12} />
                  Delivery OTP
                </div>
                <div
                  className="mt-2 flex items-center justify-center gap-1 font-display text-5xl font-black tracking-widest text-primary"
                  data-testid="delivery-otp-display"
                >
                  {String(order.delivery_otp).split("").map((c, i) => (
                    <span key={i} className="inline-block min-w-[0.9em] rounded-md bg-white px-1.5 py-0.5 shadow-inner ring-1 ring-primary/30">{c}</span>
                  ))}
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                  Share this OTP with the driver on arrival, <b>or</b> tap Confirm below to verify yourself.
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={copyOtp}
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5"
                    data-testid="btn-copy-otp"
                  >
                    <Copy size={11} /> Copy OTP
                  </button>
                  {order.otp_verified_at ? (
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700" data-testid="otp-verified-chip">
                      <CheckCircle2 size={12} /> Confirmed at {new Date(order.otp_verified_at).toLocaleTimeString()}
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => setConfirmOpen(true)} data-testid="btn-open-confirm-delivery">
                      <CheckCircle2 size={12} className="mr-1" /> Confirm delivery
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {order.status === "delivered" && (
        <Card>
          <CardHeader><CardTitle>Delivery summary</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row icon={Fuel} label="Delivered quantity" value={`${order.delivered_quantity} L`} />
            <Row icon={Clock} label="Delivered at" value={new Date(order.delivered_at).toLocaleString()} />
            {order.invoice_id && <div className="mt-2"><Link to="/portal/invoices" className="text-primary hover:underline" data-testid="link-invoice">View invoice →</Link></div>}
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="confirm-delivery-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-primary" />
              Confirm delivery
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Enter the 4-digit OTP shown above to confirm the driver has arrived and delivery is starting.
              This lets us mark the order as customer-verified.
            </p>
            <Input
              type="text"
              inputMode="numeric"
              maxLength={4}
              autoFocus
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ""))}
              placeholder="1234"
              className="text-center font-display text-2xl font-black tracking-[0.4em]"
              data-testid="confirm-otp-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={() => confirmMut.mutate(otpInput)}
              disabled={otpInput.length !== 4 || confirmMut.isPending}
              data-testid="btn-verify-confirm-delivery"
            >
              {confirmMut.isPending ? "Verifying…" : "Verify & confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ icon: Icon, label, value, sub }) => (
  <div className="flex items-start justify-between border-b border-border/40 pb-2 last:border-0">
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon size={14} />
      <span className="text-xs uppercase tracking-widest">{label}</span>
    </div>
    <div className="text-right">
      <div className="font-semibold">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  </div>
);
